import React from 'react';
import { cn } from '@/lib/utils';
import { ShieldAlert, ShieldCheck } from 'lucide-react';

interface RiskIndicatorProps {
  label: string;
  current: number;
  limit: number;
  unit?: string;
  formatValue?: (val: number) => string;
  invert?: boolean; // If true, higher is safer
  className?: string;
}

export const RiskIndicator: React.FC<RiskIndicatorProps> = ({
  label,
  current,
  limit,
  unit = '',
  formatValue,
  className,
}) => {
  const isConfigured = limit > 0;
  const percentage = isConfigured ? Math.min(100, Math.max(0, (Math.abs(current) / limit) * 100)) : 0;

  const isWarning = isConfigured && percentage >= 70 && percentage < 90;
  const isCritical = isConfigured && percentage >= 90;

  const getBarColor = () => {
    if (!isConfigured) return 'bg-slate-700';
    if (isCritical) return 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.6)]';
    if (isWarning) return 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.6)]';
    return 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]';
  };

  const formattedCurrent = formatValue ? formatValue(current) : `${current}${unit}`;
  const formattedLimit = isConfigured
    ? formatValue
      ? formatValue(limit)
      : `${limit}${unit}`
    : 'Not configured';

  return (
    <div className={cn('p-3 rounded-lg border border-slate-800/80 bg-slate-900/40 space-y-2', className)}>
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-400 font-sans flex items-center gap-1.5 font-medium">
          {isCritical ? (
            <ShieldAlert className="w-3.5 h-3.5 text-rose-400" />
          ) : (
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
          )}
          {label}
        </span>
        <span className="font-mono tabular-nums text-slate-300 font-semibold">
          {formattedCurrent} / <span className="text-slate-500">{formattedLimit}</span>
        </span>
      </div>

      {/* Progress track */}
      <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden" role="progressbar" aria-valuenow={percentage} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
        <div
          className={cn('h-full transition-all duration-500 rounded-full', getBarColor())}
          style={{ width: `${isConfigured ? percentage : 0}%` }}
        />
      </div>

      <div className="flex items-center justify-between text-[10px] font-mono text-slate-500">
        <span>0%</span>
        <span className={cn(isCritical ? 'text-rose-400 font-bold' : isWarning ? 'text-amber-400' : 'text-slate-400')}>
          {isConfigured ? `${percentage.toFixed(0)}% utilized` : '—'}
        </span>
        <span>100%</span>
      </div>
    </div>
  );
};
